import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Camera, Sun, Moon, Monitor, Bell, Shield, Database, CreditCard, HelpCircle, Info, LogOut, Tags, LayoutDashboard, Lock, Share2, KeyRound, FileText, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChangePinForm } from "@/components/VaultSave";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";
import { ExportDataDialog } from "@/components/ExportDataDialog";
import { PaywallSheet } from "@/components/PaywallSheet";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, errorMessage, absUrl } from "@/lib/api";
import { PLAN_NAMES, formatBytes } from "@/lib/format";
import { shareApp } from "@/lib/vault";

function UsageBar({ label, used, limit, testId, fmt = (v) => v }) {
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-xs"><span className="ga-muted">{label}</span><span className="font-semibold">{fmt(used)}{limit ? ` / ${fmt(limit)}` : " • ilimitado"}</span></div>
      <div className="ga-surface mt-1 h-2 overflow-hidden rounded-full"><div className="h-full rounded-full transition-all" style={{ width: limit ? `${pct}%` : "100%", background: pct > 90 ? "#E5484D" : "#13C9A3" }} /></div>
    </div>
  );
}

function Row({ icon: Icon, label, to, onClick, right, testId }) {
  const Cmp = to ? Link : "button";
  return (
    <Cmp to={to} onClick={onClick} className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-medium transition-colors hover:bg-black/[0.02]" data-testid={testId}>
      <Icon size={18} style={{ color: "#0A6373" }} /><span className="flex-1">{label}</span>{right ?? <ChevronRight size={16} className="ga-muted" />}
    </Cmp>
  );
}

export default function Profile() {
  const { user, logout, updateUser, setUser } = useAuth();
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  useEffect(() => {
    if (!user?.avatar_file_id) return setAvatarUrl(null);
    api.get(`/files/${user.avatar_file_id}/link`).then((r) => setAvatarUrl(absUrl(r.data.url))).catch(() => setAvatarUrl(null));
  }, [user?.avatar_file_id]);
  const [name, setName] = useState(user?.name || "");

  useEffect(() => { api.get("/users/me/usage").then((r) => setUsage(r.data)); }, [user?.plan]);

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post("/users/me/avatar", fd); setUser(data); toast.success("Foto atualizada."); } catch (err) { toast.error(errorMessage(err)); }
  };
  const saveName = async () => { await updateUser({ name }); toast.success("Nome atualizado."); setDialog(null); };
  const share = async () => {
    try { const r = await shareApp(); if (r === "copied") toast.success("Link copiado: https://clicouguardou.com.br"); } catch (e) { if (e?.name !== "AbortError") toast.error("Não foi possível compartilhar agora."); }
  };
  const exportData = () => {
    if (user?.plan === "free") return setPaywall("A exportação completa dos seus dados (ZIP com JSON, CSV e anexos) está disponível no Clicou, Guardou!+.");
    setExporting(true);
  };
  const [paywall, setPaywall] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  return (
    <div className="ga-fade-up px-5 pt-8">
      <h1 className="text-2xl font-semibold tracking-tight">Perfil</h1>
      <div className="ga-card mt-5 flex items-center gap-4 p-4">
        <label className="relative cursor-pointer" data-testid="profile-avatar-label">
          {avatarUrl ? <img src={avatarUrl} alt="Sua foto" className="h-16 w-16 rounded-2xl object-cover" /> : <div className="flex h-16 w-16 items-center justify-center rounded-2xl text-2xl font-semibold text-white" style={{ background: "linear-gradient(135deg,#05324A,#0A6373)" }}>{user?.name?.[0]?.toUpperCase()}</div>}
          <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full text-white" style={{ background: "#13C9A3" }}><Camera size={12} /></span>
          <input type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="profile-avatar-input" />
        </label>
        <div className="min-w-0 flex-1">
          <button onClick={() => setDialog("name")} className="block truncate text-left text-base font-semibold" data-testid="profile-name">{user?.name}</button>
          <p className="ga-muted truncate text-xs" data-testid="profile-email">{user?.email || (user?.username ? `@${user.username}` : "")}</p>
          <span className="mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold" style={{ background: user?.plan === "free" ? "var(--ga-surface)" : "#05324A", color: user?.plan === "free" ? "var(--ga-text)" : "#33D9B2" }} data-testid="profile-plan-badge">{PLAN_NAMES[user?.plan]}</span>
        </div>
      </div>

      {usage && (
        <div className="ga-card mt-3 space-y-3 p-4" data-testid="profile-usage">
          <div className="flex items-center justify-between"><h2 className="text-sm font-semibold">Uso do plano</h2>{!usage.is_premium && <Link to="/planos" className="text-xs font-semibold" style={{ color: "#13C9A3" }} data-testid="profile-upgrade-link">Guarde ainda mais</Link>}</div>
          <UsageBar label="Memórias" used={usage.memories.used} limit={usage.memories.limit} testId="usage-memories" />
          <UsageBar label="Fotos neste mês" used={usage.photos.used} limit={usage.photos.limit} testId="usage-photos" />
          <UsageBar label="Voz neste mês" used={usage.voice.used} limit={usage.voice.limit} testId="usage-voice" />
          {usage.storage && <UsageBar label="Armazenamento" used={usage.storage.used} limit={usage.storage.limit} fmt={formatBytes} testId="usage-storage" />}
        </div>
      )}

      <div className="ga-card mt-3 p-4">
        <h2 className="text-sm font-semibold">Aparência</h2>
        <div className="ga-surface mt-3 flex rounded-2xl p-1" data-testid="theme-toggle-button">
          {[["light", "Claro", Sun], ["dark", "Escuro", Moon], ["auto", "Automático", Monitor]].map(([k, l, I]) => (
            <button key={k} onClick={() => setMode(k)} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-medium" style={mode === k ? { background: "var(--ga-card)", color: "#0A6373" } : { color: "var(--ga-muted)" }} data-testid={`theme-option-${k}`} aria-pressed={mode === k}><I size={14} />{l}</button>
          ))}
        </div>
      </div>

      <div className="ga-card mt-3 divide-y overflow-hidden" style={{ borderColor: "var(--ga-border)" }}>
        <Row icon={Bell} label="Sons e notificações" to="/notificacoes" testId="profile-notifications" />
        <Row icon={KeyRound} label="Segurança" to="/seguranca" testId="profile-security" />
        <Row icon={Tags} label="Categorias" to="/categorias" testId="profile-categories" />
        <Row icon={Lock} label="Pasta Segura" to="/pasta-segura" testId="profile-vault" />
        <Row icon={MessageCircle} label="Chat seguro" to="/chat" testId="profile-chat" />
        <Row icon={KeyRound} label="Alterar PIN da Pasta Segura" onClick={() => setDialog("changePin")} testId="profile-vault-change-pin" />
        <Row icon={Shield} label="Privacidade e segurança" to="/privacidade" testId="profile-privacy" />
        <Row icon={Database} label="Exportar meus dados" onClick={exportData} testId="profile-export" />
        <Row icon={CreditCard} label="Assinatura" to="/planos" testId="profile-subscription" />
        <Row icon={HelpCircle} label="Ajuda e suporte" to="/ajuda" testId="profile-help" />
        <Row icon={Share2} label="Compartilhar o app" onClick={share} testId="profile-share-app" />
        <Row icon={Info} label="Sobre" onClick={() => setDialog("about")} testId="profile-about" />
        <Row icon={FileText} label="Termos de Uso" to="/termos" testId="profile-terms" />
        <Row icon={FileText} label="Política de Privacidade" to="/politica-de-privacidade" testId="profile-privacy-policy" />
        {user?.role === "admin" && <Row icon={LayoutDashboard} label="Painel administrativo" to="/admin" testId="profile-admin" />}
      </div>

      <button onClick={() => { logout(); navigate("/entrar"); }} className="ga-card mt-3 flex w-full items-center gap-3 px-4 py-3.5 text-sm font-medium text-red-500" data-testid="profile-logout-button"><LogOut size={18} />Sair</button>
      <button onClick={() => setDeleting(true)} className="ga-muted mt-3 w-full py-2 text-center text-xs underline" data-testid="profile-delete-account">Excluir minha conta</button>
      <DeleteAccountDialog open={deleting} onClose={() => setDeleting(false)} />
      <ExportDataDialog open={exporting} onClose={() => setExporting(false)} onPaywall={() => setPaywall("A exportação completa dos seus dados está disponível no Clicou, Guardou!+.")} />
      <PaywallSheet open={!!paywall} reason={paywall} onClose={() => setPaywall(null)} />
      <p className="ga-muted mt-6 text-center text-xs">Pequenas coisas hoje. Mais tranquilidade amanhã.</p>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          {dialog === "name" && (<><DialogTitle>Seu nome</DialogTitle><DialogDescription className="sr-only">Editar nome</DialogDescription><input value={name} onChange={(e) => setName(e.target.value)} className="ga-input" data-testid="profile-name-input" /><button onClick={saveName} className="ga-btn-primary w-full" data-testid="profile-name-save">Salvar</button></>)}
          {dialog === "changePin" && (<><DialogTitle>Alterar PIN</DialogTitle><DialogDescription className="text-sm">Informe o PIN atual e escolha um novo PIN de 4 a 8 dígitos.</DialogDescription><ChangePinForm onDone={() => setDialog(null)} /></>)}
          {dialog === "help" && (<><DialogTitle>Ajuda</DialogTitle><DialogDescription className="text-sm leading-relaxed">Para guardar algo, toque em Falar, Câmera, Escrever ou Documento. Fale como se estivesse contando para alguém. Depois, use “Pergunte ao Clicou, Guardou!” para encontrar em linguagem natural. Dúvidas: ajuda@clicouguardou.app</DialogDescription></>)}
          {dialog === "about" && (<><DialogTitle>Sobre o Clicou, Guardou!</DialogTitle><DialogDescription className="text-sm leading-relaxed">Você vive, a gente lembra. O Clicou, Guardou! é a sua memória pessoal para o cotidiano: registre em segundos, encontre em segundos. Versão 1.0 (MVP).</DialogDescription></>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
